@if (showLoading()) {
	<div class="h-full flex items-center justify-center gap-2 text-wb-muted text-xs">
		<i class="pi pi-spin pi-spinner"></i>
		Loading files
	</div>
} @else if (categories().length === 0) {
	<div class="p-3 text-wb-muted text-xs">No files found</div>
} @else {
	@for (cat of categories(); track cat.key) {
		<div
			class="category"
			[class.category--drag-over]="dragOverCategory() === cat.key"
			(dragenter)="onOsDragEnter($event, cat.key)"
			(dragover)="onOsDragOver($event, cat.key)"
			(dragleave)="onOsDragLeave($event, cat.key)"
			(drop)="onOsDrop($event, cat.key)"
		>
			<div class="category-header-row">
				<div
					class="category-header"
					role="button"
					tabindex="0"
					(click)="toggleCategory(cat.key)"
					(keydown.enter)="toggleCategory(cat.key)"
					(keydown.space)="$event.preventDefault(); toggleCategory(cat.key)"
				>
					<i [class]="cat.icon"></i>
					<span class="category-label">{{ cat.label }}</span>
					@if (cat.count > 0) {
						<span class="category-count">{{ cat.count }}</span>
					}
					@if (cat.activeCount > 0) {
						<span class="category-upload-badge" [title]="cat.activeCount + ' uploading'">
							<i class="pi pi-spin pi-spinner"></i>
							{{ cat.activeCount }}
						</span>
					}
					<div class="category-actions">
						@if (isUploadableCategory(cat.key) && !isReadOnly()) {
							@if (isVariantGroupCategory(cat.key)) {
								<button
									class="category-group-btn"
									[disabled]="creatingGroup()"
									[title]="'New variant group in ' + cat.label"
									(click)="$event.stopPropagation(); createVariantGroup($any(cat.key))"
								>
									<i class="pi pi-folder-plus"></i>
								</button>
							}
							<label class="category-upload-btn" [title]="'Upload ' + cat.label + ' file'" (click)="$event.stopPropagation()">
								<i class="pi pi-plus"></i>
								<input type="file" [accept]="acceptForCategory(cat.key)" (change)="onUploadFileSelected($event, $any(cat.key))" multiple hidden />
							</label>
						}
						<i class="pi category-expand-icon" [class]="isCollapsed(cat.key) ? 'pi-chevron-right' : 'pi-chevron-down'"></i>
					</div>
				</div>
			</div>

			@if (!isCollapsed(cat.key)) {
				@if (cat.files.length > 0) {
					<div class="media-view-toggle">
						<button class="media-toggle-btn" [class.active]="getCatView(cat.key) === 'grid'" (click)="toggleCatView(cat.key, 'grid')">
							<i class="pi pi-th-large"></i>
						</button>
						<button class="media-toggle-btn" [class.active]="getCatView(cat.key) === 'list'" (click)="toggleCatView(cat.key, 'list')">
							<i class="pi pi-list"></i>
						</button>
						<div class="media-sort-toggle">
							<button class="media-toggle-btn" [class.active]="getSortMode(cat.key) === 'alphabetical'" title="Sort alphabetically" (click)="setSortMode(cat.key, 'alphabetical')">
								<i class="pi pi-sort-alpha-down"></i>
							</button>
							<button class="media-toggle-btn" [class.active]="getSortMode(cat.key) === 'custom'" title="Custom order — drag files to reorder" (click)="setSortMode(cat.key, 'custom')">
								<i class="pi pi-arrows-v"></i>
							</button>
						</div>
					</div>
				}
				@if (getCatView(cat.key) === 'grid') {
					<div class="file-grid">
						@for (row of cat.files; track trackDisplayed($index, row)) {
							@if (isPendingUpload(row)) {
								<!-- Pending upload placeholder (Phase 3) -->
								<div
									class="file-box file-box--pending"
									[class.file-box--pending-error]="row.status() === 'error'"
									[title]="row.file.name + ' — ' + (row.error() ?? row.status())"
								>
									@if (row.status() === 'error') {
										<i class="pending-error-icon pi pi-exclamation-circle" [title]="row.error() ?? 'Upload failed'"></i>
									} @else if (row.duplicateWarning) {
										<i class="pending-dup-icon pi pi-exclamation-triangle" title="A file with this name already exists"></i>
									}
									@if (row.previewUrl) {
										<img [src]="row.previewUrl" [alt]="row.file.name" class="file-preview-img" draggable="false" />
									} @else {
										<div class="file-icon-box"><i [class]="cat.icon" class="text-xl"></i></div>
									}
									<span class="file-name" [attr.data-full-name]="row.file.name">{{ stripExt(row.file.name) }}</span>
									@if (row.status() === 'error' && !row.nonRetriable) {
										<button
											type="button"
											class="file-delete-btn file-delete-btn--retry"
											title="Retry upload"
											(click)="retryPendingUpload(row, $event)"
										>
											<i class="pi pi-refresh" style="font-size: 7px;"></i>
										</button>
									} @else {
										<button
											type="button"
											class="file-delete-btn"
											[title]="row.status() === 'error' ? 'Dismiss' : 'Cancel upload'"
											(click)="cancelPendingUpload(row, $event)"
										>
											<i class="pi pi-times" style="font-size: 7px;"></i>
										</button>
									}
								</div>
							} @else {
								@let file = asProjectFile(row);
								@if (file.variants) {
									<div
										class="file-box variant-group"
										[class.file-box--draggable]="isDraggableCategory(file) || canReorder()"
										[class.file-box--drag-over]="dragOverFileId() === file.id"
										[attr.draggable]="dragOverCategory() !== cat.key && (isDraggableCategory(file) || canReorder()) || null"
										(dragstart)="onDragStart($event, file); canReorder() && onReorderDragStart($event, file)"
										(dragover)="onFileBoxDragOver($event, file, cat.key)"
										(dragleave)="canReorder() && onReorderDragLeave(file)"
										(dragend)="canReorder() && onReorderDragEnd()"
										(drop)="onFileBoxDrop($event, file, cat.key)"
										(click)="openVariantDialog(file, $event)"
										[title]="file.name"
									>
										<div class="file-icon-box">
											<i class="pi pi-folder text-lg" style="color: var(--wb-cat-folder)"></i>
											<span class="variant-badge">{{ file.variants.length }}</span>
										</div>
										<span class="file-name" [attr.data-full-name]="file.name">{{ file.name }}</span>
										@if (!isReadOnly()) {
											<button type="button" class="file-delete-btn" title="Delete variant group + all variants" (click)="deleteVariantGroup(file, $event)">
												<i class="pi pi-times" style="font-size: 7px;"></i>
											</button>
										}
									</div>
								} @else {
									<div
										class="file-box"
										[class.file-box--draggable]="isDraggableCategory(file) || canReorder()"
										[class.file-box--just-uploaded]="file.correlationId"
										[class.file-box--drag-over]="dragOverFileId() === file.id"
										[attr.draggable]="dragOverCategory() !== cat.key && (isDraggableCategory(file) || canReorder()) || null"
										(dragstart)="onDragStart($event, file); canReorder() && onReorderDragStart($event, file)"
										(dragover)="onFileBoxDragOver($event, file, cat.key)"
										(dragleave)="canReorder() && onReorderDragLeave(file)"
										(dragend)="canReorder() && onReorderDragEnd()"
										(drop)="onFileBoxDrop($event, file, cat.key)"
										[title]="file.name"
										(mouseenter)="hoveredFile.set(file)"
										(mouseleave)="hoveredFile.set(null)"
									>
										@if (file.duplicateWarning) {
											<i class="pending-dup-icon pi pi-exclamation-triangle" title="A file with this name already exists"></i>
										}
										@if (isImageCategory(file)) {
											<img [src]="file.previewUrl" [alt]="file.name" class="file-preview-img" loading="lazy" draggable="false" />
										}
										@if (isVideoCategory(file)) {
											@if (isHovered(file) && file.videoUrl) {
												<video class="file-preview-video" [src]="file.videoUrl" autoplay muted loop playsinline draggable="false"></video>
											} @else if (file.previewUrl !== file.videoUrl) {
												<img [src]="file.previewUrl" [alt]="file.name" class="file-preview-img" loading="lazy" draggable="false" />
											} @else {
												<div class="file-icon-box"><i [class]="cat.icon" class="text-lg"></i></div>
											}
										}
										@if (!isImageCategory(file) && !isVideoCategory(file)) {
											<div class="file-icon-box"><i [class]="cat.icon" class="text-xl"></i></div>
										}
										<span class="file-name" [attr.data-full-name]="file.name">{{ stripExt(file.name) }}</span>
										@if (!isReadOnly()) {
											<button type="button" class="file-delete-btn" [title]="isLocalFile(file) ? 'Delete uploaded file' : 'Hide from media pool'" (click)="deleteLocalFile(file, $event)">
												<i class="pi pi-times" style="font-size: 7px;"></i>
											</button>
										}
									</div>
								}
							}
						}
						@empty {
							<div class="file-grid-empty px-3 py-2 text-center text-wb-muted text-[12px]">No {{ cat.label }} files yet.</div>
						}
					</div>
				} @else {
					<div class="file-list">
						@for (row of cat.files; track trackDisplayed($index, row)) {
							@if (isPendingUpload(row)) {
								<!-- Pending upload placeholder — list view -->
								<div
									class="file-list-row file-list-row--pending"
									[class.file-list-row--pending-error]="row.status() === 'error'"
									[title]="row.file.name + ' — ' + (row.error() ?? row.status())"
								>
									@if (row.previewUrl) {
										<img [src]="row.previewUrl" class="file-list-thumb" draggable="false" />
									} @else {
										<i [class]="cat.icon" class="file-list-icon"></i>
									}
									<span class="file-list-name">
										@if (row.duplicateWarning && row.status() !== 'error') {
											<i class="pi pi-exclamation-triangle file-list-dup-icon" title="A file with this name already exists"></i>
										}
										{{ stripExt(row.file.name) }}
									</span>
									<span class="file-list-status">
										@switch (row.status()) {
											@case ('uploading') { Uploading... }
											@case ('probing') { Probing... }
											@case ('queued') {
												@if (row.retryAfterMs() !== null) { Retrying... } @else { Queued }
											}
											@case ('error') { Error }
										}
									</span>
									@if (row.status() === 'error' && !row.nonRetriable) {
										<button
											type="button"
											class="file-list-delete file-list-delete--retry"
											title="Retry upload"
											(click)="retryPendingUpload(row, $event)"
										>
											<i class="pi pi-refresh" style="font-size: 7px;"></i>
										</button>
									} @else {
										<button
											type="button"
											class="file-list-delete"
											[title]="row.status() === 'error' ? 'Dismiss' : 'Cancel upload'"
											(click)="cancelPendingUpload(row, $event)"
										>
											<i class="pi pi-times" style="font-size: 7px;"></i>
										</button>
									}
								</div>
							} @else {
								@let file = asProjectFile(row);
								<div
									class="file-list-row"
									[class.file-box--draggable]="isDraggableCategory(file) || canReorder()"
									[class.file-list-row--just-uploaded]="file.correlationId"
									[class.file-list-row--drag-over]="dragOverFileId() === file.id"
									[attr.draggable]="dragOverCategory() !== cat.key && (isDraggableCategory(file) || canReorder()) || null"
									(dragstart)="onDragStart($event, file); canReorder() && onReorderDragStart($event, file)"
									(dragover)="onFileBoxDragOver($event, file, cat.key)"
									(dragleave)="canReorder() && onReorderDragLeave(file)"
									(dragend)="canReorder() && onReorderDragEnd()"
									(drop)="onFileBoxDrop($event, file, cat.key)"
									[title]="file.name"
								>
									@if (!file.variants && (isVideoCategory(file) || isImageCategory(file)) && file.previewUrl !== file.videoUrl) {
									<img [src]="file.previewUrl" class="file-list-thumb" draggable="false" />
								} @else {
									<i [class]="file.variants ? 'pi pi-folder' : cat.icon" class="file-list-icon"></i>
								}
									<span class="file-list-name">
										@if (file.duplicateWarning) {
											<i class="pi pi-exclamation-triangle file-list-dup-icon" title="A file with this name already exists"></i>
										}
										{{ file.variants ? file.name : stripExt(file.name) }}
									</span>
									@if (file.variants) {
										<span class="file-list-badge">{{ file.variants.length }}</span>
									}
									@if (file.variants) {
										<button type="button" class="file-list-delete" (click)="openVariantDialog(file, $event)" title="View variants">
											<i class="pi pi-external-link" style="font-size: 7px;"></i>
										</button>
									} @else if (!isReadOnly()) {
										<button type="button" class="file-list-delete" (click)="deleteLocalFile(file, $event)" title="Delete">
											<i class="pi pi-times" style="font-size: 7px;"></i>
										</button>
									}
								</div>
							}
						}
						@empty {
							<div class="px-3 py-2 text-center text-wb-muted text-[12px]">No {{ cat.label }} files yet.</div>
						}
					</div>
				}
			}
		</div>
	}
}

<!-- Variant dialog (PrimeNG — renders at body level, avoids overflow clipping) -->
<p-dialog
	[header]="variantDialogFile()?.name ?? 'Variants'"
	[visible]="variantDialogVisible()"
	(visibleChange)="$event ? null : closeVariantDialog()"
	[modal]="true"
	[dismissableMask]="true"
	[style]="{ width: '600px', maxHeight: '80vh' }"
	appendTo="body"
>
	@if (variantDialogFile(); as dialogFile) {
		@if (dialogFile.isLocal && !isReadOnly()) {
			<div class="mb-3 flex gap-2 items-center">
				<label class="text-[10px] text-wb-muted uppercase font-semibold shrink-0">Name</label>
				<input
					pInputText
					class="flex-1 text-xs"
					[value]="dialogFile.name"
					(change)="renameGroup($any($event.target).value)"
				/>
			</div>
		}
		<div class="mb-3 flex gap-2">
			<input
				pInputText
				class="flex-1"
				placeholder="Filter variants..."
				[ngModel]="variantFilter()"
				(ngModelChange)="variantFilter.set($event)"
			/>
			@if (dialogFile.isLocal && !isReadOnly()) {
				<label class="variant-upload-btn" [class.variant-upload-btn--disabled]="uploadingVariants()">
					@if (uploadingVariants()) {
						<i class="pi pi-spin pi-spinner text-xs"></i>
					} @else {
						<i class="pi pi-plus text-xs mr-1"></i> Add files
					}
					<input type="file" [accept]="acceptForCategory(dialogFile.category)" [disabled]="uploadingVariants()" (change)="onVariantUploadSelected($event)" multiple hidden />
				</label>
			}
		</div>
		<div class="variant-grid">
			@for (v of filteredVariants(); track v.encodedName) {
				<div class="variant-box" [title]="v.url">
					<div class="variant-icon-box">
						<i [class]="getCategoryIcon(dialogFile.category)" class="text-base"></i>
					</div>
					<span class="variant-box-name">{{ v.name }}</span>
					@if (dialogFile.isLocal && !isReadOnly()) {
						<button
							type="button"
							class="variant-delete-btn"
							title="Delete variant"
							(click)="deleteVariant(v, $event)"
						>
							<i class="pi pi-times text-[8px]"></i>
						</button>
					}
				</div>
			}
			@if (filteredVariants().length === 0) {
				<div class="p-3 text-center text-wb-muted text-xs col-span-full">No variants yet</div>
			}
		</div>
		<div class="mt-3 text-right text-[0.7rem] text-wb-muted">
			{{ filteredVariants().length }} of {{ dialogFile.variants!.length }} variants
		</div>
	}
</p-dialog>

<app-upload-duplicate-dialog
	[visible]="duplicateDialogVisible()"
	[duplicates]="duplicateDialogRows()"
	[nonDuplicates]="duplicateDialogNonDuplicates()"
	(visibleChange)="onDuplicateDialogVisibleChange($event)"
	(confirmed)="onDuplicateDialogConfirm($event)"
	(cancelled)="onDuplicateDialogCancel()"
></app-upload-duplicate-dialog>
